<div class="genieSettingsWrapper">
  <div class="backButton" (click)="builderService.navigateBack()">
    <img src="/assets/icons/arrow-left.svg" [alt]="'Go back' | translate" />
  </div>
  <div class="header-section">
    <div>
      <p class="title">{{ 'Commerce & Orders' | translate }}</p>
      <p class="subtitle">{{ 'Configure your store logic, inventory visibility, and shipping behavior.' | translate }}</p>
    </div>
    
    <button class="saveButton" (click)="saveSettings()" [class.loading]="settingsProgress" [disabled]="settingsProgress">
      {{ 'Save Changes' | translate }}
    </button>
  </div>

  <form [formGroup]="settingsForm" class="generalContainer">
    
    <!-- Top Row: Commerce (2/3) and Localization (1/3) -->
    <div class="top-row-grid">
      
      <!-- Commerce Card -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ 'Commerce' | translate }}</h3>
        </div>
        <div class="card-body">
          <div class="fields-grid">
            
            <!-- Currency -->
            <div class="field-wrapper">
              <div class="field-label-row"><label>{{ 'Currency' | translate }}</label></div>
              <div class="genie-input">
                <app-search-select formControlName="currency" [options]="currencyOptions" [placeholder]="'Search currency' | translate"></app-search-select>
              </div>
            </div>

            <!-- Company Country -->
            <div class="field-wrapper">
              <div class="field-label-row"><label>{{ 'Company Country' | translate }}</label></div>
              <div class="genie-input">
                <app-search-select formControlName="companyCountry" [options]="companyCountryOptions" [placeholder]="'Search country' | translate"></app-search-select>
              </div>
            </div>

            <!-- Default Country -->
            <div class="field-wrapper">
              <div class="field-label-row"><label>{{ 'Default Country' | translate }}</label></div>
              <div class="genie-input">
                <app-search-select formControlName="defaultCountry" [options]="defaultCountryOptions" [placeholder]="'Select a default country' | translate"></app-search-select>
              </div>
            </div>

            <!-- Country Option -->
            <div class="field-wrapper">
              <div class="field-label-row"><label>{{ 'Select Country Option' | translate }}</label></div>
              <div class="genie-input">
                <select formControlName="countryOptions">
                  <option value="all">{{ 'All Countries' | translate }}</option>
                  <option value="multi">{{ 'Multi Countries' | translate }}</option>
                  <option value="single">{{ 'Single Country' | translate }}</option>
                </select>
              </div>
            </div>

            <!-- Multi Countries -->
            <div class="field-wrapper" *ngIf="settingsForm.get('countryOptions')?.value === 'multi'">
              <div class="field-label-row"><label>{{ 'Select Countries' | translate }}</label></div>
              <div class="genie-input">
                <select formControlName="selectedCountries" multiple size="1">
                  <option *ngFor="let country of countries" [value]="country.dial_code">{{ country.emoji }} {{ country.name }} ({{ country.dial_code }})</option>
                </select>
              </div>
            </div>

            <!-- Single Country Code -->
            <div class="field-wrapper" *ngIf="settingsForm.get('countryOptions')?.value === 'single'">
              <div class="field-label-row"><label>{{ 'Enter Country Code' | translate }}</label></div>
              <div class="genie-input">
                <input type="text" formControlName="countryNumber" placeholder="20" (input)="startWithPlus($event)">
              </div>
            </div>

            <!-- Initial Order Status & Shipping Options (Grid 2 cols) -->
            <div class="fields-grid-2">
               
               <!-- Initial Order Status -->
               <div class="field-wrapper">
                 <div class="field-label-row"><label>{{ 'Initial Order Status' | translate }}</label></div>
                 <div class="genie-input">
                    <select formControlName="initial_order_status">
                      <option value="Order Received">{{ 'Order Received' | translate }}</option>
                      <option value="Processing">{{ 'Processing' | translate }}</option>
                      <option value="Pending">{{ 'Pending' | translate }}</option>
                    </select>
                 </div>
               </div>

               <!-- Shipping Options -->
               <div class="field-wrapper">
                 <div class="field-label-row"><label>{{ 'Shipping Options' | translate }}</label></div>
                 <div class="genie-input">
                   <select formControlName="shippingOption">
                     <option value="show">{{ 'Show' | translate }}</option>
                     <option value="free">{{ 'Free shipping' | translate }}</option>
                     <option value="hide">{{ 'Hide' | translate }}</option>
                     <option value="contact">{{ 'Contact for shipping' | translate }}</option>
                   </select>
                 </div>
               </div>
            </div>

            <!-- Estimated Delivery -->
            <div class="field-wrapper">
              <div class="field-label-row"><label>{{ 'Estimated Delivery' | translate }}</label></div>
              <div class="estimated-delivery-group">
                <div class="genie-input flex-grow">
                   <input type="number" formControlName="estimatedDeliveryTime" placeholder="2">
                </div>
                <div class="genie-input width-auto">
                   <select formControlName="estimatedDeliveryUnit">
                     <option value="minutes">{{ 'Minutes' | translate }}</option>
                     <option value="hours">{{ 'Hours' | translate }}</option>
                     <option value="days">{{ 'Days' | translate }}</option>
                     <option value="weeks">{{ 'Weeks' | translate }}</option>
                   </select>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Localization & Maps Card -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ 'Localization & Maps' | translate }}</h3>
        </div>
        <div class="card-body">
          <div class="fields-grid">
            
            <!-- Google Maps Enablement -->
            <div class="field-wrapper toggle-row">
              <div>
                <label>{{ 'Google Maps Enablement' | translate }}</label>
                <p class="description">{{ 'Use Google Maps for store location and delivery address picking' | translate }}</p>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" formControlName="enableGoogleMaps">
                <span class="toggle-slider"></span>
              </label>
            </div>

            <!-- Show Country -->
            <div class="field-wrapper toggle-row">
              <div>
                <label>{{ 'Show Country' | translate }}</label>
                <p class="description">{{ 'Show country selection in address forms' | translate }}</p>
              </div>
              <label class="toggle-switch">
                 <input type="checkbox" formControlName="showCountry">
                 <span class="toggle-slider"></span>
              </label>
            </div>

            <!-- Show Area -->
            <div class="field-wrapper toggle-row">
              <div>
                 <label>{{ 'Show Area' | translate }}</label>
                 <p class="description">{{ 'Show area selection in address forms' | translate }}</p>
              </div>
              <label class="toggle-switch">
                 <input type="checkbox" formControlName="showArea">
                 <span class="toggle-slider"></span>
              </label>
            </div>

          </div>
        </div>
      </div>

    </div>

    <!-- Store Behavior Card (Full Width) -->
    <div class="section-card">
      <div class="card-header">
         <h3>{{ 'Store Behavior' | translate }}</h3>
      </div>
      <div class="card-body">
         <div class="fields-grid">
           
           <!-- Hide out-of-stock -->
           <div class="field-wrapper toggle-row">
             <div>
                <label>{{ 'Hide out-of-stock products' | translate }}</label>
                <p class="description">{{ 'Remove items from the catalog automatically when stock reaches zero' | translate }}</p>
             </div>
             <label class="toggle-switch">
                <input type="checkbox" formControlName="hideOutStockProduct">
                <span class="toggle-slider"></span>
             </label>
           </div>

           <!-- Show product stock -->
           <div class="field-wrapper toggle-row">
             <div>
                <label>{{ 'Show product stock' | translate }}</label>
                <p class="description">{{ 'Display the available stock quantity on product pages' | translate }}</p>
             </div>
             <label class="toggle-switch">
                <input type="checkbox" formControlName="showProductStock">
                <span class="toggle-slider"></span>
             </label>
           </div>

           <!-- Use variant image -->
           <div class="field-wrapper toggle-row">
             <div>
                <label>{{ 'Use variant image as main image' | translate }}</label>
                <p class="description">{{ 'Show specific variant photo when selected by the user' | translate }}</p>
             </div>
             <label class="toggle-switch">
                <input type="checkbox" formControlName="useVariantImageAsMain">
                <span class="toggle-slider"></span>
             </label>
           </div>

           <!-- Allow file upload -->
           <div class="field-wrapper toggle-row">
             <div>
                <label>{{ 'Allow file upload' | translate }}</label>
                <p class="description">{{ 'Let customers attach files to their orders (customization/notes)' | translate }}</p>
             </div>
             <label class="toggle-switch">
                <input type="checkbox" formControlName="allowUpload">
                <span class="toggle-slider"></span>
             </label>
           </div>

           <!-- Enable order booking -->
           <div class="field-wrapper toggle-row" [class.no-border]="settingsForm.get('enableOrderBooking')?.value">
             <div>
                <label>{{ 'Enable order booking' | translate }}</label>
                <p class="description">{{ 'Allow customers to reserve items for later checkout' | translate }}</p>
             </div>
             <label class="toggle-switch">
                <input type="checkbox" formControlName="enableOrderBooking">
                <span class="toggle-slider"></span>
             </label>
           </div>
          
           <!-- Schedule Settings Component (Shown when Order Booking enabled) -->
           <div class="nested-settings" *ngIf="settingsForm.get('enableOrderBooking')?.value">
             <app-schedule-settings (scheduleChange)="onScheduleChange($event)"></app-schedule-settings>
           </div>

           <!-- Email on order -->
           <div class="field-wrapper toggle-row" [class.no-border]="settingsForm.get('enableOrderEmail')?.value">
             <div>
                <label>{{ 'Email on order' | translate }}</label>
                <p class="description">{{ 'Send automated confirmation to customer and admin' | translate }}</p>
             </div>
             <label class="toggle-switch">
                <input type="checkbox" formControlName="enableOrderEmail">
                <span class="toggle-slider"></span>
             </label>
           </div>

           <!-- Email Settings (Shown when Email enabled) -->
           <div class="nested-settings" *ngIf="settingsForm.get('enableOrderEmail')?.value">
              <div class="fields-grid">
                 <div class="field-wrapper">
                    <div class="field-label-row"><label>{{ 'Sender Email' | translate }}</label></div>
                    <p class="description">{{ 'Emails will be sent from this address. Leave empty to use the project owner email.' | translate }}</p>
                    <div class="genie-input">
                       <input type="email" formControlName="senderEmail" [placeholder]="'noreply@yourdomain.com' | translate">
                    </div>
                 </div>
                 <div class="field-wrapper">
                    <div class="field-label-row"><label>{{ 'Email Language' | translate }}</label></div>
                    <p class="description">{{ 'Applies only if Subject/Template below are left empty.' | translate }}</p>
                    <div class="genie-input">
                       <select formControlName="emailLanguage">
                          <option value="en">{{ 'English' | translate }}</option>
                          <option value="ar">{{ 'Arabic' | translate }}</option>
                       </select>
                    </div>
                 </div>
                 <div class="field-wrapper">
                    <div class="field-label-row"><label>{{ 'Email Subject' | translate }}</label></div>
                    <div class="genie-input">
                       <input type="text" formControlName="emailSubject">
                    </div>
                 </div>
                 <div class="field-wrapper">
                    <div class="field-label-row"><label>{{ 'Email Signature' | translate }}</label></div>
                    <div class="genie-input">
                       <input type="text" formControlName="emailSignature">
                    </div>
                 </div>
                 <div class="field-wrapper">
                    <div class="field-label-row"><label>{{ 'Email Template' | translate }}</label></div>
                    <div class="genie-textarea">
                       <textarea formControlName="emailTemplate" rows="6"></textarea>
                    </div>
                 </div>
              </div>
           </div>

         </div>
      </div>
    </div>

  </form>
</div>
